import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Plus, Search, X, Save, Edit2, Trash2, 
  MessageSquareText, Briefcase, ChevronRight,
  Loader2, Info, ArrowLeft, Filter, Tag as TagIcon,
  FolderTree, Palette, Check, Settings2, Eye,
  Camera, LucideImage, ExternalLink, Bold, List,
  Image as ImageIcon, CloudUpload, LayoutList,
  ChevronDown, Layers, Map, Target,
  RefreshCw
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { InspectionNarrative, Service, NarrativeTag, NarrativeCategory } from '../types';
import { supabase, toSnakeCase, toCamelCase, getErrorMessage } from '../lib/supabase';

interface NarrativeProps {
  narratives: InspectionNarrative[];
  setNarratives: React.Dispatch<React.SetStateAction<InspectionNarrative[]>>;
  services: Service[];
}

const InspectionNarratives: React.FC<NarrativeProps> = ({ 
  narratives, setNarratives, services 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [parentCategoryFilter, setParentCategoryFilter] = useState('');
  const [subCategoryFilter, setSubCategoryFilter] = useState('');
  
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isTaxonomyOpen, setIsTaxonomyOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Taxonomy State
  const [tags, setTags] = useState<NarrativeTag[]>([]);
  const [categories, setCategories] = useState<NarrativeCategory[]>([]);
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [tagForm, setTagForm] = useState({ name: '', color: '#3b82f6' });
  const [editingCatId, setEditingCatId] = useState<string | null>(null);
  const [catForm, setCatForm] = useState({ name: '', parentId: '' });

  const [formData, setFormData] = useState<Partial<InspectionNarrative>>({
    title: '',
    content: '',
    serviceId: '',
    categoryId: '',
    parentCategoryId: '',
    tagIds: [],
    images: []
  });

  useEffect(() => {
    fetchTaxonomy();
  }, []);

  const fetchTaxonomy = async () => {
    try {
      const { data: tagData, error: tagError } = await supabase.from('narrative_tags').select('*');
      if (tagError) throw tagError;
      if (tagData) setTags(tagData.map(toCamelCase));

      const { data: catData, error: catError } = await supabase.from('narrative_categories').select('*');
      if (catError) throw catError;
      if (catData) setCategories(catData.map(toCamelCase));
    } catch (err) {
      console.error("Taxonomy Sync Failure:", getErrorMessage(err));
    }
  };

  const parentCategories = useMemo(() => categories.filter(c => !c.parentId), [categories]);
  const subCategories = useMemo(() => categories.filter(c => !!c.parentId), [categories]);

  const filtered = narratives.filter(n => {
    const matchesSearch = n.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         (n.content || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesService = serviceFilter ? n.serviceId === serviceFilter : true;
    const matchesParent = parentCategoryFilter ? n.parentCategoryId === parentCategoryFilter : true;
    const matchesSub = subCategoryFilter ? n.categoryId === subCategoryFilter : true;
    return matchesSearch && matchesService && matchesParent && matchesSub;
  });

  const handleOpenAdd = () => {
    setEditingId(null);
    setFormData({
      title: '',
      content: '',
      serviceId: services[0]?.id || '',
      categoryId: '',
      parentCategoryId: '',
      tagIds: [],
      images: []
    });
    setIsSidebarOpen(true);
  };

  const handleOpenEdit = (nar: InspectionNarrative, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setEditingId(nar.id);
    setFormData({ ...nar });
    setIsSidebarOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title || !formData.serviceId) return;
    setSaving(true);

    try {
      const payload = toSnakeCase(formData);
      if (editingId) {
        const { error } = await supabase.from('inspection_narratives').update(payload).eq('id', editingId);
        if (error) throw error;
        setNarratives(prev => prev.map(n => n.id === editingId ? { ...n, ...formData } as InspectionNarrative : n));
      } else {
        const { data, error } = await supabase.from('inspection_narratives').insert([payload]).select().single();
        if (error) throw error;
        setNarratives(prev => [toCamelCase(data), ...prev]);
      }
      setIsSidebarOpen(false);
    } catch (err) {
      alert(`Registry Update Failure: ${getErrorMessage(err)}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!confirm('Permanently remove this technical logic entry?')) return;
    try {
      const { error } = await supabase.from('inspection_narratives').delete().eq('id', id);
      if (error) throw error;
      setNarratives(prev => prev.filter(n => n.id !== id));
    } catch (err) { alert(getErrorMessage(err)); }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const timestamp = Date.now();
      const fileName = `narrative-${timestamp}-${file.name.replace(/\s+/g, '_')}`;
      const { error } = await supabase.storage.from('narrative-media').upload(fileName, file);
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from('narrative-media').getPublicUrl(fileName);
      setFormData(prev => ({ ...prev, images: [...(prev.images || []), publicUrl] }));
    } catch (err) { alert(getErrorMessage(err)); }
    finally { setUploadingImage(false); }
  };

  const handleSaveTag = async () => {
    if (!tagForm.name) return;
    try {
      const payload = toSnakeCase(tagForm);
      if (editingTagId) {
        await supabase.from('narrative_tags').update(payload).eq('id', editingTagId);
        setTags(prev => prev.map(t => t.id === editingTagId ? { ...t, ...tagForm } : t));
      } else {
        const { data, error } = await supabase.from('narrative_tags').insert([payload]).select().single();
        if (error) throw error;
        if (data) setTags(prev => [...prev, toCamelCase(data)]);
      }
      setEditingTagId(null);
      setTagForm({ name: '', color: '#3b82f6' });
    } catch (err) { alert(`Tag Sync Error: ${getErrorMessage(err)}`); }
  };

  const handleSaveCat = async () => {
    if (!catForm.name) return;
    setSaving(true);
    try {
      const payload = toSnakeCase({
        name: catForm.name,
        parentId: catForm.parentId || null
      });

      if (editingCatId) {
        // Validation: Cannot be its own parent
        if (editingCatId === catForm.parentId) {
          throw new Error("Hierarchy conflict: An entry cannot reference itself as a parent.");
        }
        
        const { error } = await supabase.from('narrative_categories').update(payload).eq('id', editingCatId);
        if (error) throw error;
      } else {
        const { data, error } = await supabase.from('narrative_categories').insert([payload]).select().single();
        if (error) throw error;
      }
      
      await fetchTaxonomy();
      setEditingCatId(null);
      setCatForm({ name: '', parentId: '' });
    } catch (err) { 
      alert(`Category Sync Error: ${getErrorMessage(err)}`); 
    } finally {
      setSaving(false);
    }
  };

  const toggleTag = (tagId: string) => {
    setFormData(prev => {
      const current = prev.tagIds || [];
      return {
        ...prev,
        tagIds: current.includes(tagId) ? current.filter(id => id !== tagId) : [...current, tagId]
      };
    });
  };

  return (
    <div className="animate-in fade-in duration-300 pb-20">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h3 className="text-xl font-black text-slate-800 tracking-tight uppercase">Technical Registry</h3>
          <p className="text-slate-500 text-sm font-medium">Standardized hierarchical observation logic for technical field sessions.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setIsTaxonomyOpen(true)} className="p-2.5 bg-white border border-slate-200 rounded-xl text-slate-500 hover:text-[#606c38] transition-all shadow-sm"><Settings2 size={20} /></button>
          <button onClick={handleOpenAdd} className="bg-slate-900 text-white px-6 py-2.5 rounded-xl font-bold text-[10px] uppercase tracking-widest shadow-lg hover:bg-slate-800 transition-all flex items-center gap-2"><Plus size={18} /> New Protocol</button>
        </div>
      </div>

      <div className="flex flex-col xl:flex-row gap-4 mb-8">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input type="text" placeholder="Search technical library..." className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl shadow-sm outline-none focus:ring-1 focus:ring-[#606c38] transition-all text-sm font-bold" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <div className="flex flex-wrap md:flex-nowrap gap-3">
          <div className="relative w-full md:w-48">
            <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
            <select className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl shadow-sm outline-none appearance-none focus:ring-1 focus:ring-[#606c38] transition-all text-[9px] font-black uppercase tracking-widest" value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)}>
              <option value="">All Services</option>
              {services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="relative w-full md:w-56">
            <LayoutList className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
            <select className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl shadow-sm outline-none appearance-none focus:ring-1 focus:ring-[#606c38] transition-all text-[9px] font-black uppercase tracking-widest" value={parentCategoryFilter} onChange={(e) => { setParentCategoryFilter(e.target.value); setSubCategoryFilter(''); }}>
              <option value="">All Systems</option>
              {parentCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="relative w-full md:w-56">
            <Target className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
            <select className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-xl shadow-sm outline-none appearance-none focus:ring-1 focus:ring-[#606c38] transition-all text-[9px] font-black uppercase tracking-widest" value={subCategoryFilter} onChange={(e) => setSubCategoryFilter(e.target.value)}>
              <option value="">All Components</option>
              {subCategories.filter(c => parentCategoryFilter ? c.parentId === parentCategoryFilter : true).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden overflow-x-auto no-scrollbar">
        <table className="w-full text-left min-w-max">
          <thead>
            <tr className="bg-slate-50/50 border-b border-slate-200">
              <th className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase tracking-widest">Logic Identity</th>
              <th className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase tracking-widest">Protocol</th>
              <th className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase tracking-widest">System Taxonomy</th>
              <th className="px-6 py-4 text-[10px] font-bold text-slate-500 uppercase tracking-widest text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map(nar => (
              <tr key={nar.id} onClick={(e) => handleOpenEdit(nar, e)} className="hover:bg-slate-50/80 transition-colors cursor-pointer group">
                <td className="px-6 py-4 max-w-md">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400 group-hover:bg-[#606c38]/10 group-hover:text-[#606c38] transition-colors shrink-0">
                      <MessageSquareText size={18} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-slate-800 truncate">{nar.title}</p>
                      <div className="flex gap-1 mt-1">
                        {nar.tagIds?.map(tid => {
                          const tag = tags.find(t => t.id === tid);
                          return tag ? <span key={tid} className="w-2 h-2 rounded-full" style={{ backgroundColor: tag.color }}></span> : null;
                        })}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-[10px] font-black uppercase text-[#606c38] tracking-tighter">{services.find(s => s.id === nar.serviceId)?.name || 'Global'}</span>
                </td>
                <td className="px-6 py-4">
                   <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1.5">
                        <Layers size={10} className="text-blue-500" />
                        <span className="text-[10px] font-bold text-slate-700 uppercase tracking-tight">{parentCategories.find(c => c.id === nar.parentCategoryId)?.name || 'General System'}</span>
                      </div>
                      <div className="flex items-center gap-1.5 ml-3">
                        <Target size={10} className="text-slate-300" />
                        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{subCategories.find(c => c.id === nar.categoryId)?.name || 'Unclassified Component'}</span>
                      </div>
                   </div>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={(e) => handleDelete(nar.id, e)} className="p-2 text-slate-300 hover:text-red-500 rounded-lg hover:bg-red-50 transition-all"><Trash2 size={16} /></button>
                    <ChevronRight size={16} className="text-slate-200" />
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={4} className="px-6 py-20 text-center text-slate-400">
                  <Info size={40} className="mx-auto mb-3 opacity-10" />
                  <p className="text-xs font-bold uppercase tracking-[0.2em]">No matching protocol logic indexed</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Taxonomy Management Modal */}
      {isTaxonomyOpen && (
        <div className="fixed inset-0 z-[500] flex items-center justify-center p-6">
           <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setIsTaxonomyOpen(false)} />
           <div className="bg-white w-full max-w-3xl rounded-3xl shadow-2xl relative overflow-hidden flex flex-col max-h-[85vh] animate-in zoom-in-95 duration-300">
              <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                 <div className="flex items-center gap-3"><div className="p-2 bg-slate-900 text-white rounded-lg"><Settings2 size={20} /></div><h3 className="font-black text-slate-900 text-sm uppercase tracking-widest">System Taxonomy Architect</h3></div>
                 <button onClick={() => setIsTaxonomyOpen(false)} className="p-2 hover:bg-white rounded-full text-slate-400 transition-colors"><X size={24} /></button>
              </div>
              <div className="p-8 overflow-y-auto no-scrollbar space-y-12">
                 <section className="space-y-6">
                    <SectionHeader icon={FolderTree} title="Hierarchy Management" color="olive" />
                    <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100 space-y-4">
                       <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{editingCatId ? 'Modify Taxonomy Entry' : 'New Taxonomy Entry'}</p>
                       <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <input className="w-full p-3 bg-white border border-slate-200 rounded-xl text-sm font-bold" placeholder="Entry Label..." value={catForm.name} onChange={(e) => setCatForm({...catForm, name: e.target.value})} />
                          <select className="w-full p-3 bg-white border border-slate-200 rounded-xl text-sm font-bold" value={catForm.parentId} onChange={(e) => setCatForm({...catForm, parentId: e.target.value})}>
                             <option value="">Create Primary System</option>
                             {parentCategories.filter(c => c.id !== editingCatId).map(c => (
                               <option key={c.id} value={c.id}>Sub of: {c.name}</option>
                             ))}
                          </select>
                       </div>
                       <div className="flex gap-2">
                          {editingCatId && (
                            <button onClick={() => { setEditingCatId(null); setCatForm({ name: '', parentId: '' }); }} className="flex-1 py-3 bg-slate-200 text-slate-600 rounded-xl text-xs font-black uppercase tracking-widest">Abort</button>
                          )}
                          <button disabled={saving} onClick={handleSaveCat} className="flex-[2] py-3 bg-[#606c38] text-white rounded-xl text-xs font-black uppercase tracking-widest shadow-lg flex items-center justify-center gap-2">
                            {saving ? <Loader2 size={16} className="animate-spin" /> : (editingCatId ? <RefreshCw size={16} /> : <Plus size={16} />)} 
                            {editingCatId ? 'Update Taxonomy' : 'Deploy Taxonomy'}
                          </button>
                       </div>
                    </div>

                    <div className="space-y-6">
                       {parentCategories.map(parent => (
                          <div key={parent.id} className="space-y-3">
                             <div className="flex items-center justify-between p-4 bg-slate-100 rounded-xl group">
                                <div className="flex items-center gap-3">
                                   <Layers size={14} className="text-blue-500" />
                                   <span className="text-sm font-black text-slate-800 uppercase tracking-tight">{parent.name}</span>
                                </div>
                                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                   <button onClick={() => { setEditingCatId(parent.id); setCatForm({ name: parent.name, parentId: '' }); }} className="p-1.5 text-slate-400 hover:text-blue-600 transition-colors"><Edit2 size={14} /></button>
                                   <button onClick={async () => { if(confirm('Purge primary system and all sub-components?')) { await supabase.from('narrative_categories').delete().eq('id', parent.id); fetchTaxonomy(); } }} className="p-1.5 text-slate-300 hover:text-red-500 transition-colors"><Trash2 size={14} /></button>
                                </div>
                             </div>
                             <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pl-8">
                                {subCategories.filter(sub => sub.parentId === parent.id).map(sub => (
                                   <div key={sub.id} className="flex items-center justify-between p-3 bg-white border border-slate-100 rounded-xl group hover:border-blue-200 transition-all">
                                      <div className="flex items-center gap-2">
                                         <Target size={12} className="text-slate-300" />
                                         <span className="text-xs font-bold text-slate-600">{sub.name}</span>
                                      </div>
                                      <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                         <button onClick={() => { setEditingCatId(sub.id); setCatForm({ name: sub.name, parentId: sub.parentId || '' }); }} className="text-slate-400 hover:text-blue-600"><Edit2 size={12} /></button>
                                         <button onClick={async () => { if(confirm('Purge sub-component?')) { await supabase.from('narrative_categories').delete().eq('id', sub.id); fetchTaxonomy(); } }} className="text-slate-200 hover:text-red-400"><X size={14} /></button>
                                      </div>
                                   </div>
                                ))}
                             </div>
                          </div>
                       ))}
                    </div>
                 </section>

                 <section className="space-y-6">
                    <SectionHeader icon={TagIcon} title="Industrial Tag Registry" color="indigo" />
                    <div className="p-6 bg-slate-50 rounded-2xl border border-slate-100 space-y-4">
                       <div className="flex gap-3">
                          <input className="flex-1 p-3 bg-white border border-slate-200 rounded-xl text-sm font-bold" placeholder="Tag Label..." value={tagForm.name} onChange={(e) => setTagForm({...tagForm, name: e.target.value})} />
                          <input type="color" className="w-12 h-12 rounded-xl cursor-pointer bg-transparent border-none p-0" value={tagForm.color} onChange={(e) => setTagForm({...tagForm, color: e.target.value})} />
                          <button onClick={handleSaveTag} className="px-8 bg-indigo-600 text-white rounded-xl text-xs font-black uppercase tracking-widest"><Plus size={16} /></button>
                       </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                       {tags.map(t => (
                          <div key={t.id} className="px-4 py-2 rounded-xl border border-slate-100 flex items-center gap-3 group transition-all hover:shadow-md bg-white" style={{ borderLeft: `4px solid ${t.color}` }}>
                             <span className="text-[10px] font-black uppercase tracking-widest text-slate-700">{t.name}</span>
                             <button onClick={async () => { if(confirm('Purge tag?')) { await supabase.from('narrative_tags').delete().eq('id', t.id); fetchTaxonomy(); } }} className="text-slate-200 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"><Trash2 size={12} /></button>
                          </div>
                       ))}
                    </div>
                 </section>
              </div>
           </div>
        </div>
      )}

      {/* Narrative Sidebar */}
      <div className={`fixed inset-0 z-[300] ${isSidebarOpen ? 'visible' : 'invisible'}`}>
        <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setIsSidebarOpen(false)} />
        <div className={`absolute inset-y-0 right-0 w-full md:max-w-4xl bg-white shadow-2xl transition-transform duration-500 ${isSidebarOpen ? 'translate-x-0' : 'translate-x-full'} flex flex-col`}>
          <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
            <h3 className="font-bold text-slate-800 text-xs uppercase tracking-widest">{editingId ? 'Modify Technical Protocol' : 'New Protocol Registry'}</h3>
            <button onClick={() => setIsSidebarOpen(false)} className="p-2 hover:bg-white rounded-xl text-slate-400 transition-all"><X size={24} /></button>
          </div>
          
          <form onSubmit={handleSave} className="flex-1 p-8 space-y-10 overflow-y-auto no-scrollbar bg-white">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
               <div className="lg:col-span-7 space-y-6">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Logic Headline</label>
                    <input required className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl text-base font-bold text-slate-800 outline-none focus:bg-white focus:ring-1 focus:ring-[#606c38] transition-all shadow-sm" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder="Industrial finding identifier..." />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Primary System</label>
                      <select required className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold text-slate-800 outline-none focus:bg-white focus:ring-1 focus:ring-[#606c38] transition-all" value={formData.parentCategoryId} onChange={(e) => setFormData({...formData, parentCategoryId: e.target.value, categoryId: ''})}>
                        <option value="">Choose System...</option>
                        {parentCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Sub-Component</label>
                      <select required className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold text-slate-800 outline-none focus:bg-white focus:ring-1 focus:ring-[#606c38] transition-all" value={formData.categoryId} onChange={(e) => setFormData({...formData, categoryId: e.target.value})} disabled={!formData.parentCategoryId}>
                        <option value="">Choose Component...</option>
                        {subCategories.filter(c => c.parentId === formData.parentCategoryId).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Mapping Protocol</label>
                    <select required className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold text-slate-800 outline-none focus:bg-white focus:ring-1 focus:ring-[#606c38] transition-all" value={formData.serviceId} onChange={(e) => setFormData({...formData, serviceId: e.target.value})}>
                      <option value="">Global Mapping</option>
                      {services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
               </div>

               <div className="lg:col-span-5 space-y-8">
                  <div className="space-y-4">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] block">Taxonomy Tags</label>
                    <div className="flex flex-wrap gap-2 p-5 bg-slate-50 border border-slate-100 rounded-[2rem] max-h-[160px] overflow-y-auto no-scrollbar shadow-inner">
                       {tags.map(t => (
                          <button key={t.id} type="button" onClick={() => toggleTag(t.id)} className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2 ${(formData.tagIds || []).includes(t.id) ? 'bg-white border-2 border-indigo-500 shadow-md scale-105' : 'bg-white border border-slate-200 opacity-60 hover:opacity-100'}`}>
                             <div className="w-2.5 h-2.5 rounded-full shadow-sm" style={{ backgroundColor: t.color }}></div>
                             {t.name}
                          </button>
                       ))}
                    </div>
                  </div>

                  <div className="space-y-4">
                     <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] block">Static Technical Media</label>
                     <div className="grid grid-cols-3 gap-2">
                        {formData.images?.map((url, idx) => (
                           <div key={idx} className="relative aspect-square rounded-2xl overflow-hidden border border-slate-200 group shadow-sm">
                              <img src={url} className="w-full h-full object-cover transition-transform group-hover:scale-110" />
                              <button onClick={() => setFormData(prev => ({...prev, images: prev.images?.filter((_, i) => i !== idx)}))} className="absolute inset-0 bg-rose-500/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={18} /></button>
                           </div>
                        ))}
                        <button type="button" onClick={() => fileInputRef.current?.click()} className="aspect-square bg-slate-50 border-2 border-dashed border-slate-200 rounded-2xl flex flex-col items-center justify-center text-slate-300 hover:text-[#606c38] hover:border-[#606c38] transition-all group">
                           {uploadingImage ? <Loader2 size={24} className="animate-spin" /> : <Camera size={24} className="group-hover:scale-110 transition-transform" />}
                           <span className="text-[9px] font-black uppercase mt-2">Add Plate</span>
                        </button>
                        <input ref={fileInputRef} type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                     </div>
                  </div>
               </div>
            </div>

            <div className="space-y-4">
               <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                  <label className="text-[11px] font-black text-slate-900 uppercase tracking-[0.3em] flex items-center gap-2"><Bold size={14} className="text-[#606c38]" /> Logic Markup Specification</label>
                  <div className="flex gap-4 text-[9px] font-black text-slate-400 uppercase tracking-widest"><span># Headers</span><span>**Importance**</span><span>* Items</span></div>
               </div>
               <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-[450px]">
                  <textarea required className="h-full w-full p-8 bg-slate-50 border border-slate-200 rounded-[2rem] font-mono text-[13px] leading-relaxed text-slate-800 outline-none focus:bg-white focus:ring-1 focus:ring-[#606c38] resize-none transition-all shadow-inner" value={formData.content} onChange={(e) => setFormData({...formData, content: e.target.value})} placeholder="Draft technical specification using industrial Markdown syntax..." />
                  <div className="h-full w-full p-8 bg-slate-900 border border-slate-800 rounded-[2rem] overflow-y-auto no-scrollbar text-white prose prose-invert prose-sm max-w-none shadow-2xl">
                     <ReactMarkdown remarkPlugins={[remarkGfm]}>{formData.content || '*Registry Logic Engine awaiting technical input...*'}</ReactMarkdown>
                  </div>
               </div>
            </div>
          </form>
          <div className="p-8 border-t border-slate-100 flex gap-4 shadow-inner bg-white">
            <button type="button" onClick={() => setIsSidebarOpen(false)} className="flex-1 py-4 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:bg-slate-50 rounded-2xl border border-slate-200 transition-all">Cancel</button>
            <button disabled={saving} onClick={handleSave} className="flex-[2] py-4 bg-slate-900 text-white font-black text-[10px] uppercase tracking-widest rounded-2xl hover:bg-slate-800 transition-all flex items-center justify-center gap-2 shadow-2xl">{saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} Deploy Protocol</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SectionHeader = ({ icon: Icon, title, color = "olive" }: any) => {
  let colorClass = "text-[#606c38]";
  if (color === "indigo") colorClass = "text-indigo-600";
  return (
    <div className="flex items-center gap-3 border-b border-slate-100 pb-3">
      <div className={`${colorClass} p-2 bg-slate-50 rounded-xl shadow-sm`}><Icon size={16} strokeWidth={2.5} /></div>
      <h4 className="text-[11px] font-black text-slate-700 uppercase tracking-[0.2em]">{title}</h4>
    </div>
  );
};

export default InspectionNarratives;